import FlipOutlinedIcon from "@mui/icons-material/FlipOutlined"
import Rotate90DegreesCwOutlinedIcon from "@mui/icons-material/Rotate90DegreesCwOutlined"
import Box from "@mui/material/Box"
import type { AnySceneObjectT, GroupStyleI } from "tweenly"
import { flipLineRotation } from "../../../../helpers/line.helpers"
import {
    flipScale,
    getRotation,
    roundTo2,
    setRotation,
} from "../../../../helpers/transform.helpers"
import { useObjectActions } from "../../../../hooks/useObjectActions"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import SegmentedButtonGroup from "../../../common/PropertyTools/SegmentedButtonGroup"

interface Props {
    object: AnySceneObjectT
}

/**
 * Rotation as in Figma's Position section: the angle, a quarter turn and the two flips.
 *
 * The angle is the object's rotateZ (clockwise, like CSS). A line flips by turning, so its
 * scale stays 1 and its end-point handles keep matching what is drawn; anything else flips
 * by the sign of its scale.
 */
const Rotation = ({ object }: Props) => {
    const { updateObject } = useObjectActions()
    const rotation = roundTo2(getRotation(object.transforms))
    const isLine = object.type === "line"

    const writeTransforms = (transforms: GroupStyleI[]) =>
        updateObject(object.id, "transforms", transforms)

    const handleRotationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value)
        if (Number.isFinite(value) && value !== rotation)
            writeTransforms(setRotation(object.transforms, value))
    }
    const handleFlip = (axis: "x" | "y") =>
        writeTransforms(
            isLine ? flipLineRotation(object.transforms, axis) : flipScale(object.transforms, axis)
        )

    return (
        // two equal columns: the angle, and the three buttons joined into one field
        <Box display="flex" gap={1} width="100%" alignItems="stretch">
            <Box flex={1} minWidth={0}>
                <PrefixedInput
                    name="rotation"
                    value={rotation}
                    label="rotation (deg)"
                    tooltip="Rotation (deg)"
                    handleOnChange={handleRotationChange}
                    min={-360}
                    max={360}
                />
            </Box>
            <Box flex={1} minWidth={0} display="flex">
                <SegmentedButtonGroup
                    segments={[
                        {
                            title: "Rotate 90°",
                            icon: <Rotate90DegreesCwOutlinedIcon fontSize="small" />,
                            onClick: () =>
                                writeTransforms(setRotation(object.transforms, rotation + 90)),
                        },
                        {
                            title: "Flip horizontal",
                            icon: <FlipOutlinedIcon fontSize="small" />,
                            onClick: () => handleFlip("x"),
                        },
                        {
                            title: "Flip vertical",
                            icon: (
                                <FlipOutlinedIcon
                                    fontSize="small"
                                    sx={{ transform: "rotate(90deg)" }}
                                />
                            ),
                            onClick: () => handleFlip("y"),
                        },
                    ]}
                />
            </Box>
        </Box>
    )
}

export default Rotation
